import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import { toast } from 'react-hot-toast';
import { Store, Clock, Globe, FileText, Image, Palette, Upload, Loader } from 'lucide-react';

interface DaySchedule {
  open: string;
  close: string;
  isClosed?: boolean;
  is24h?: boolean;
  lunchBreak?: {
    start: string;
    end: string;
  };
}

interface BusinessHours {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
  isOnlineStore?: boolean;
}

interface Banner {
  imageUrl: string;
  linkUrl: string;
  title: string;
}

interface StoreColors {
  primary: string;
  secondary: string;
  accent: string;
}

interface StoreSettings {
  slug: string;
  url?: string;
  customDomain?: string;
  hasPurchasedDomain?: boolean;
  isDomainActive?: boolean;
  description: string;
  businessHours: BusinessHours;
  banners: Banner[];
  colors: StoreColors;
  vat: string;
  address: string;
  bankAccount: {
    iban: string;
  };
  commercialEmail: string;
  commercialPhone: string;
  district: string;
}

const defaultBusinessHours: BusinessHours = {
  monday: { open: '09:00', close: '18:00', lunchBreak: { start: '13:00', end: '14:00' } },
  tuesday: { open: '09:00', close: '18:00', lunchBreak: { start: '13:00', end: '14:00' } },
  wednesday: { open: '09:00', close: '18:00', lunchBreak: { start: '13:00', end: '14:00' } },
  thursday: { open: '09:00', close: '18:00', lunchBreak: { start: '13:00', end: '14:00' } },
  friday: { open: '09:00', close: '18:00', lunchBreak: { start: '13:00', end: '14:00' } },
  saturday: { open: '09:00', close: '13:00' },
  sunday: { open: '', close: '', isClosed: true },
  isOnlineStore: false
};

const defaultSettings: StoreSettings = {
  slug: '',
  url: '',
  customDomain: '',
  hasPurchasedDomain: false,
  isDomainActive: false,
  description: '',
  businessHours: defaultBusinessHours,
  banners: [
    { imageUrl: '', linkUrl: '', title: '' },
    { imageUrl: '', linkUrl: '', title: '' },
    { imageUrl: '', linkUrl: '', title: '' }
  ],
  colors: {
    primary: '#4F46E5',
    secondary: '#1F2937',
    accent: '#EF4444'
  },
  vat: '',
  address: '',
  bankAccount: {
    iban: ''
  },
  commercialEmail: '',
  commercialPhone: '',
  district: ''
};

export default function StoreSettings() {
  const [settings, setSettings] = useState<StoreSettings>(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(true);
  const [uploadingImage, setUploadingImage] = useState<{ [key: number]: boolean }>({});
  const { currentUser } = useAuth();
  const { balance, updateBalance } = useWallet();
  const navigate = useNavigate();

  useEffect(() => {
    loadSettings();
  }, [currentUser]);

  const loadSettings = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const storeRef = doc(db, 'stores', currentUser.uid);
      const storeDoc = await getDoc(storeRef);
      
      if (storeDoc.exists()) {
        const data = storeDoc.data();
        setSettings({
          slug: data.slug || '',
          url: data.url || '',
          customDomain: data.customDomain || '',
          hasPurchasedDomain: data.hasPurchasedDomain || false,
          isDomainActive: data.isDomainActive || false,
          description: data.description || '',
          businessHours: data.businessHours || defaultBusinessHours,
          banners: data.banners || defaultSettings.banners,
          colors: data.colors || defaultSettings.colors,
          vat: data.vat || '',
          address: data.address || '',
          bankAccount: data.bankAccount || { iban: '' },
          commercialEmail: data.commercialEmail || '',
          commercialPhone: data.commercialPhone || '',
          district: data.district || ''
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const storeRef = doc(db, 'stores', currentUser.uid);
      
      await setDoc(storeRef, {
        ...settings,
        url: `https://controlo.emtuacasa.pt/shop/${settings.slug}`
      }, { merge: true });

      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file: File, bannerIndex: number) => {
    if (!file) return;
    
    try {
      setUploadingImage(prev => ({ ...prev, [bannerIndex]: true }));
      
      const imageRef = ref(storage, `stores/${currentUser.uid}/banners/${Date.now()}_${file.name}`);
      await uploadBytes(imageRef, file);
      const imageUrl = await getDownloadURL(imageRef);
      
      const newBanners = [...settings.banners];
      newBanners[bannerIndex].imageUrl = imageUrl;
      setSettings(prev => ({ ...prev, banners: newBanners }));
      
      toast.success('Imagem carregada com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      toast.error('Erro ao carregar imagem');
    } finally {
      setUploadingImage(prev => ({ ...prev, [bannerIndex]: false }));
    }
  };

  const handleInputChange = (field: keyof StoreSettings, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-6">
          <Store className="h-6 w-6 text-primary mr-2" />
          <h1 className="text-2xl font-bold text-secondary">Configurações da Loja Online</h1>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
          {/* Informação Básica */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center">
                <Store className="h-5 w-5 text-primary mr-2" />
                <h2 className="text-lg font-medium text-secondary">Informação Básica</h2>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                  Slug da Loja
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                    https://controlo.emtuacasa.pt/shop/
                  </span>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={settings.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    className={`flex-1 min-w-0 block w-full px-3 py-2 rounded-r-md ${
                      !slugAvailable ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary'
                    } focus:ring focus:ring-opacity-50 sm:text-sm`}
                    placeholder="minhaloja"
                    required
                  />
                </div>
                {!slugAvailable && (
                  <p className="mt-1 text-sm text-red-600">Este URL já está em uso</p>
                )}
                <div className="mt-2 text-sm text-gray-600 space-y-1">
                  <p>Este é o endereço gratuito da sua loja que será disponibilizado automaticamente.</p>
                  <p>Os seus clientes podem usar este endereço para acessar a loja sem nenhum custo adicional.</p>
                  <p>Se desejar um domínio personalizado, você pode configurá-lo na seção abaixo.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Descrição */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center">
                <FileText className="h-5 w-5 text-primary mr-2" />
                <h2 className="text-lg font-medium text-secondary">Descrição da Loja</h2>
              </div>
            </div>

            <div className="p-4">
              <textarea
                id="description"
                name="description"
                value={settings.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                placeholder="Descreva sua loja..."
              />
            </div>
          </div>

          {/* Banners */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Image className="h-5 w-5 text-primary mr-2" />
                  <h2 className="text-lg font-medium text-secondary">Banners da Loja</h2>
                </div>
                <p className="text-sm text-gray-500">Adicione até 3 banners para sua loja online</p>
              </div>
            </div>

            <div className="p-4 space-y-6">
              {settings.banners.map((banner, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-700">Banner {index + 1}</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor={`banner-title-${index}`} className="block text-sm font-medium text-gray-700">
                        Título do Banner
                      </label>
                      <input
                        id={`banner-title-${index}`}
                        type="text"
                        value={banner.title}
                        onChange={(e) => {
                          const newBanners = [...settings.banners];
                          newBanners[index].title = e.target.value;
                          setSettings(prev => ({ ...prev, banners: newBanners }));
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        placeholder="Ex: Promoção de Verão"
                      />
                    </div>

                    <div>
                      <label htmlFor={`banner-image-${index}`} className="block text-sm font-medium text-gray-700">
                        Imagem do Banner
                      </label>
                      <div className="mt-1 flex items-center space-x-4">
                        {banner.imageUrl && (
                          <img
                            src={banner.imageUrl}
                            alt={banner.title || `Banner ${index + 1}`}
                            className="h-20 w-32 object-cover rounded-md"
                          />
                        )}
                        <div className="flex-1">
                          <label
                            htmlFor={`banner-image-${index}`}
                            className={`
                              relative cursor-pointer bg-white rounded-md font-medium 
                              text-primary hover:text-primary-dark focus-within:outline-none
                              ${uploadingImage[index] ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                          >
                            <div className="px-4 py-2 border border-gray-300 rounded-md flex items-center space-x-2">
                              {uploadingImage[index] ? (
                                <>
                                  <Loader className="h-5 w-5 animate-spin" />
                                  <span>Carregando...</span>
                                </>
                              ) : (
                                <>
                                  <Upload className="h-5 w-5" />
                                  <span>Carregar Imagem</span>
                                </>
                              )}
                            </div>
                            <input
                              id={`banner-image-${index}`}
                              type="file"
                              className="sr-only"
                              accept="image/*"
                              disabled={uploadingImage[index]}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleImageUpload(file, index);
                                }
                              }}
                            />
                          </label>
                          <p className="mt-1 text-xs text-gray-500">
                            PNG, JPG até 5MB
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor={`banner-url-${index}`} className="block text-sm font-medium text-gray-700">
                        URL de Destino
                      </label>
                      <input
                        id={`banner-url-${index}`}
                        type="url"
                        value={banner.linkUrl}
                        onChange={(e) => {
                          const newBanners = [...settings.banners];
                          newBanners[index].linkUrl = e.target.value;
                          setSettings(prev => ({ ...prev, banners: newBanners }));
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        placeholder="https://exemplo.com/pagina"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cores da Loja */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center">
                <Palette className="h-5 w-5 text-primary mr-2" />
                <h2 className="text-lg font-medium text-secondary">Cores da Loja</h2>
              </div>
            </div>

            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Cor Principal */}
              <div>
                <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-700">
                  Cor Principal
                </label>
                <div className="mt-1 flex items-center space-x-2">
                  <input
                    id="primaryColor"
                    type="color"
                    value={settings.colors.primary}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        colors: { ...prev.colors, primary: e.target.value }
                      }));
                    }}
                    className="h-8 w-8 rounded-md border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.colors.primary}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        colors: { ...prev.colors, primary: e.target.value }
                      }));
                    }}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
              </div>

              {/* Cor Secundária */}
              <div>
                <label htmlFor="secondaryColor" className="block text-sm font-medium text-gray-700">
                  Cor Secundária
                </label>
                <div className="mt-1 flex items-center space-x-2">
                  <input
                    id="secondaryColor"
                    type="color"
                    value={settings.colors.secondary}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        colors: { ...prev.colors, secondary: e.target.value }
                      }));
                    }}
                    className="h-8 w-8 rounded-md border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.colors.secondary}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        colors: { ...prev.colors, secondary: e.target.value }
                      }));
                    }}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
              </div>

              {/* Cor de Destaque */}
              <div>
                <label htmlFor="accentColor" className="block text-sm font-medium text-gray-700">
                  Cor de Destaque
                </label>
                <div className="mt-1 flex items-center space-x-2">
                  <input
                    id="accentColor"
                    type="color"
                    value={settings.colors.accent}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        colors: { ...prev.colors, accent: e.target.value }
                      }));
                    }}
                    className="h-8 w-8 rounded-md border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.colors.accent}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        colors: { ...prev.colors, accent: e.target.value }
                      }));
                    }}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Domínio Personalizado */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center">
                <Globe className="h-5 w-5 text-primary mr-2" />
                <h2 className="text-lg font-medium text-secondary">Domínio Personalizado</h2>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label htmlFor="customDomain" className="block text-sm font-medium text-gray-700">
                  Domínio Personalizado
                </label>
                <div className="mt-1">
                  <input
                    id="customDomain"
                    type="text"
                    value={settings.customDomain}
                    onChange={(e) => handleInputChange('customDomain', e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    placeholder="minhaloja.com.br"
                    disabled={!settings.hasPurchasedDomain}
                  />
                </div>
                {!settings.hasPurchasedDomain ? (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          if (!currentUser) return;
                          
                          const domainPrice = 9.99; // Price in EUR
                          
                          // Check if user has enough balance
                          if (balance < domainPrice) {
                            toast.error('Saldo insuficiente na wallet. Por favor, faça uma recarga.');
                            return;
                          }

                          // Create wallet transaction
                          const walletRef = collection(db, 'walletTransactions');
                          await addDoc(walletRef, {
                            userId: currentUser.uid,
                            type: 'debit',
                            amount: domainPrice,
                            description: 'Compra de Domínio Personalizado',
                            createdAt: new Date().toISOString(),
                            status: 'completed'
                          });

                          // Update user's wallet balance
                          await updateBalance(balance - domainPrice);
                          
                          // Update store settings with domain purchase
                          const storeRef = doc(db, 'stores', currentUser.uid);
                          await setDoc(storeRef, {
                            ...settings,
                            hasPurchasedDomain: true,
                            isDomainActive: false // Will be activated after domain setup
                          }, { merge: true });

                          // Create domain request
                          const requestsRef = collection(db, 'price_requests');
                          await addDoc(requestsRef, {
                            userId: currentUser.uid,
                            userName: currentUser.name || '',
                            companyName: settings.name || '',
                            message: 'Pedido de configuração de domínio personalizado',
                            status: 'pending',
                            createdAt: new Date().toISOString(),
                            type: 'domain',
                            customDomain: settings.customDomain || ''
                          });
                          
                          // Update the local state
                          setSettings(prev => ({
                            ...prev,
                            hasPurchasedDomain: true,
                            isDomainActive: false
                          }));
                          
                          // Show success message
                          toast.success('Domínio personalizado adquirido com sucesso! Em breve entraremos em contato para configurá-lo.');
                        } catch (error) {
                          console.error('Error purchasing domain:', error);
                          toast.error('Erro ao adquirir domínio. Por favor, tente novamente.');
                        }
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      Comprar Domínio Personalizado (9,99€)
                    </button>
                    <p className="mt-2 text-sm text-gray-500">
                      Com um domínio personalizado, sua loja terá uma aparência mais profissional e será mais fácil de ser lembrada pelos clientes.
                    </p>
                  </div>
                ) : !settings.isDomainActive ? (
                  <div className="mt-2 text-sm text-yellow-600">
                    Seu domínio está em processo de ativação. Isso pode levar até 48 horas.
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-green-600">
                    Seu domínio personalizado está ativo e funcionando.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Horários de Funcionamento */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-primary mr-2" />
                  <h2 className="text-lg font-medium text-secondary">Horários de Funcionamento</h2>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isOnlineStore"
                    checked={settings.businessHours.isOnlineStore}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        businessHours: {
                          ...prev.businessHours,
                          isOnlineStore: e.target.checked
                        }
                      }));
                    }}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="isOnlineStore" className="text-sm text-gray-700">
                    Loja Online (24h)
                  </label>
                </div>
              </div>
            </div>

            <div className="p-4">
              {!settings.businessHours.isOnlineStore && (
                <div className="space-y-4">
                  {Object.entries(settings.businessHours).map(([day, schedule]) => {
                    if (day === 'isOnlineStore') return null;
                    const dayName = {
                      monday: 'Segunda-feira',
                      tuesday: 'Terça-feira',
                      wednesday: 'Quarta-feira',
                      thursday: 'Quinta-feira',
                      friday: 'Sexta-feira',
                      saturday: 'Sábado',
                      sunday: 'Domingo'
                    }[day];

                    return (
                      <div key={day} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`${day}-closed`}
                            checked={!schedule.isClosed}
                            onChange={(e) => {
                              const newBusinessHours = { ...settings.businessHours };
                              newBusinessHours[day] = {
                                ...schedule,
                                isClosed: !e.target.checked
                              };
                              setSettings(prev => ({
                                ...prev,
                                businessHours: newBusinessHours
                              }));
                            }}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label htmlFor={`${day}-closed`} className="text-sm font-medium text-gray-700">
                            {dayName}
                          </label>
                        </div>

                        {!schedule.isClosed && (
                          <>
                            <div>
                              <label htmlFor={`${day}-open`} className="block text-sm text-gray-500">
                                Abertura
                              </label>
                              <input
                                type="time"
                                id={`${day}-open`}
                                value={schedule.open}
                                onChange={(e) => {
                                  const newBusinessHours = { ...settings.businessHours };
                                  newBusinessHours[day] = {
                                    ...schedule,
                                    open: e.target.value
                                  };
                                  setSettings(prev => ({
                                    ...prev,
                                    businessHours: newBusinessHours
                                  }));
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                              />
                            </div>

                            <div>
                              <label htmlFor={`${day}-close`} className="block text-sm text-gray-500">
                                Fechamento
                              </label>
                              <input
                                type="time"
                                id={`${day}-close`}
                                value={schedule.close}
                                onChange={(e) => {
                                  const newBusinessHours = { ...settings.businessHours };
                                  newBusinessHours[day] = {
                                    ...schedule,
                                    close: e.target.value
                                  };
                                  setSettings(prev => ({
                                    ...prev,
                                    businessHours: newBusinessHours
                                  }));
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                              />
                            </div>

                            <div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={`${day}-lunch`}
                                  checked={!!schedule.lunchBreak}
                                  onChange={(e) => {
                                    const newBusinessHours = { ...settings.businessHours };
                                    newBusinessHours[day] = {
                                      ...schedule,
                                      lunchBreak: e.target.checked ? { start: '13:00', end: '14:00' } : undefined
                                    };
                                    setSettings(prev => ({
                                      ...prev,
                                      businessHours: newBusinessHours
                                    }));
                                  }}
                                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                />
                                <label htmlFor={`${day}-lunch`} className="text-sm text-gray-500">
                                  Pausa para Almoço
                                </label>
                              </div>

                              {schedule.lunchBreak && (
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                  <input
                                    type="time"
                                    value={schedule.lunchBreak.start}
                                    onChange={(e) => {
                                      const newBusinessHours = { ...settings.businessHours };
                                      newBusinessHours[day] = {
                                        ...schedule,
                                        lunchBreak: {
                                          ...schedule.lunchBreak,
                                          start: e.target.value
                                        }
                                      };
                                      setSettings(prev => ({
                                        ...prev,
                                        businessHours: newBusinessHours
                                      }));
                                    }}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                                  />
                                  <input
                                    type="time"
                                    value={schedule.lunchBreak.end}
                                    onChange={(e) => {
                                      const newBusinessHours = { ...settings.businessHours };
                                      newBusinessHours[day] = {
                                        ...schedule,
                                        lunchBreak: {
                                          ...schedule.lunchBreak,
                                          end: e.target.value
                                        }
                                      };
                                      setSettings(prev => ({
                                        ...prev,
                                        businessHours: newBusinessHours
                                      }));
                                    }}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                                  />
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {settings.businessHours.isOnlineStore && (
                <p className="text-sm text-gray-600">
                  Sua loja está configurada para funcionar 24 horas por dia, 7 dias por semana.
                  Os clientes podem fazer pedidos a qualquer momento.
                </p>
              )}
            </div>
          </div>

          {/* Botão de Salvar */}
          <div className="flex justify-end mt-6">
            <button
              type="submit"
              disabled={saving}
              className={`
                px-4 py-2 rounded-md text-white
                ${saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'}
                focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50
              `}
            >
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
